import { MealSwapFilterOptionWithSubFilters } from "@/clients/models/MealSwapFilterOptionWithSubFilters";
import { MealTime } from "@/clients/models/MealTime";
import { Divider } from "@/components/divider/divider";
import { useGetAlternativeFilterOptions } from "@/hooks/requests/plan/use-get-alternative-filter-options";
import { useSearchParams } from "@/hooks/router/use-search-params";
import { IonSkeletonText, useIonRouter } from "@ionic/react";
import { memo, useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import {
  DEFAULT_FILTER,
  FILTER_PARAM,
  SUB_FILTER_PARAM,
  getEffectiveFilter,
  getEffectiveSubFilter,
} from "../../utils/swap-plan-default-filter";
import { SubFilterButtons } from "./sub-filter-buttons";
import { TopFilterButtons } from "./top-filter-buttons";

interface SwapPlanFiltersProps {
  mealTime?: MealTime;
}

export const SwapPlanFilters = memo(function SwapPlanFilters({ mealTime }: SwapPlanFiltersProps) {
  const searchParams = useSearchParams();
  const navigation = useIonRouter();
  const location = useLocation();

  const selectedTopFilter = getEffectiveFilter(searchParams);
  const selectedSubFilter = getEffectiveSubFilter(searchParams);

  const { options, optionLabelMapping, isLoading } = useGetAlternativeFilterOptions(mealTime, "v2");

  const orderedTopFilterOptions = useMemo(() => {
    const lifestyleOptions = options ?? [];
    const preferredOrder = ["recipes", "branded", "outAndAbout"];
    const ordered = preferredOrder
      .map((value) => lifestyleOptions.find((option) => option.value === value))
      .filter((option): option is MealSwapFilterOptionWithSubFilters => !!option);
    return ordered.length > 0 ? ordered : lifestyleOptions;
  }, [options]);

  const selectedTopFilterOption = useMemo(() => {
    if (!selectedTopFilter) {
      return null;
    }
    return orderedTopFilterOptions.find((option) => option.value === selectedTopFilter) ?? null;
  }, [orderedTopFilterOptions, selectedTopFilter]);

  const activeSubFilters = useMemo(() => {
    return selectedTopFilterOption?.subFilters ?? [];
  }, [selectedTopFilterOption]);

  const handleTopFilterSelect = (value: string) => {
    if (value === selectedTopFilter) {
      return;
    }
    applyTopFilter(value);
  };

  const handleSubFilterSelect = (value: string) => {
    if (value === selectedSubFilter) {
      applySubFilter(undefined);
      return;
    }
    applySubFilter(value);
  };

  const applyTopFilter = useCallback(
    (value?: string) => {
      const params = new URLSearchParams(searchParams.toString());

      if (value) {
        params.set(FILTER_PARAM, value);
      } else {
        params.delete(FILTER_PARAM);
      }

      params.delete(SUB_FILTER_PARAM);
      params.delete("fromBrand");

      const nextQuery = params.toString();
      const nextUrl = nextQuery ? `${location.pathname}?${nextQuery}` : location.pathname;

      navigation.push(nextUrl, "forward", "replace");
    },
    [location.pathname, navigation, searchParams],
  );

  const applySubFilter = useCallback(
    (value?: string) => {
      const params = new URLSearchParams(searchParams.toString());

      if (value) {
        params.set(SUB_FILTER_PARAM, value);
      } else {
        params.delete(SUB_FILTER_PARAM);
      }

      const nextQuery = params.toString();
      const nextUrl = nextQuery ? `${location.pathname}?${nextQuery}` : location.pathname;

      navigation.push(nextUrl, "forward", "replace");
    },
    [location.pathname, navigation, searchParams],
  );

  useEffect(() => {
    if (!options || options.length === 0) {
      return;
    }
    if (!searchParams.get(FILTER_PARAM)) {
      applyTopFilter(DEFAULT_FILTER);
    }
  }, [options, searchParams, applyTopFilter]);

  if (isLoading) {
    return (
      <div className="flex w-full flex-col gap-4.5 px-4">
        <IonSkeletonText animated className="h-9.5 w-full rounded-xl" />
        <div className="relative -right-4 -left-4 w-screen">
          <Divider />
        </div>
        <IonSkeletonText animated className="h-9.5 w-full rounded-xl" />
      </div>
    );
  }

  if (!mealTime || isLoading || options.length === 0) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-4.5 px-4">
      <TopFilterButtons
        options={orderedTopFilterOptions}
        selectedValue={selectedTopFilter}
        labels={optionLabelMapping}
        onSelect={handleTopFilterSelect}
      />

      <div className="relative -right-4 -left-4 w-screen">
        <Divider />
      </div>

      <SubFilterButtons
        options={activeSubFilters}
        selectedValue={selectedSubFilter}
        onSelect={handleSubFilterSelect}
      />
    </div>
  );
});
